import { useTranslation } from 'react-i18next';
import { AutoStories } from '@mui/icons-material';
import { Card, CardProps } from '@mui/material';
import { Theme } from '@mui/material/styles';

import { Content, Product, isProduct } from '../models';
import { getImageUrlForContent, getImageUrlForProduct } from '../network/useNetwork';
import { isImageMime, isVideo } from '../utils/files';

const SIZE_MAP = {
  small: 24,
  medium: 40,
  large: 64,
};

interface Props {
  item: Product | Content | undefined;
  size?: 'small' | 'medium' | 'large' | number;
  /** When true, use alt="" and hide the image from AT (adjacent text already describes the item). */
  decorative?: boolean;
}

export const ContentThumbnail = ({
  item,
  size = 'medium',
  decorative = false,
  sx,
  ...rest
}: Props & CardProps) => {
  const { t } = useTranslation();
  const dimension = typeof size === 'number' ? size : SIZE_MAP[size];

  const isItemProduct = !item || isProduct(item);

  const displayName = item
    ? isProduct(item)
      ? (item.bookTitle || item.name || '').trim()
      : (item.name || '').trim()
    : '';

  /** Meaningful alt only when non-decorative and we have a real title; otherwise empty (decorative). */
  const imageAlt =
    decorative || !item || !displayName ? '' : t('coverImageOf', { name: displayName });

  const getCardBgColor = (theme: Theme) => {
    if (isItemProduct) {
      return theme.palette.background.paper;
    }

    return isVideo(item.mimeType) || isImageMime(item.mimeType)
      ? theme.palette.common.black
      : theme.palette.common.white;
  };

  return (
    <Card
      {...rest}
      sx={[
        (theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: dimension,
          height: dimension,
          flexShrink: 0,
          overflow: 'hidden',
          borderRadius: '4px',
          backgroundColor: getCardBgColor(theme),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {item ? (
        <img
          src={isItemProduct ? getImageUrlForProduct(item) : getImageUrlForContent(item)}
          alt={imageAlt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: isItemProduct ? 'cover' : 'contain',
            objectPosition: isItemProduct ? 'top' : 'center',
            overflowClipMargin: 'unset', // Preserves image quality
          }}
        />
      ) : (
        <AutoStories
          fontSize={typeof size === 'string' ? size : undefined}
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: typeof size === 'number' ? size : undefined,
          })}
        />
      )}
    </Card>
  );
};
